import { useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { hasPermission } from '@blimo/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, CommentForm, CommentThreads, ErrorState, Label, Select, Skeleton, toast } from '@blimo/ui';

/**
 * Alle Kommentare der Prüfung (inkl. ausgeblendeter) als Threads. Antworten und neue Kommentare mit
 * `document-checks.manage`, Aus-/Einblenden mit `comments.moderate`.
 * @param {{ check: any, documents: Array<any>, me: any }} props
 */
export function CommentsTab({ check, documents, me }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery(adminQueries.documentCheckComments(api, check.id));
  const [filter, setFilter] = useState('');
  const filterId = useId();
  const canWrite = hasPermission(me, 'document-checks.manage');
  const canModerate = hasPermission(me, 'comments.moderate');
  const titleOf = (id) => {
    const doc = documents.find((d) => String(d.id) === String(id));
    return doc ? doc.title || doc.original_name : `Dokument #${id}`;
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.admin.sub('document-checks', check.id, 'comments') });

  /** @param {{ document_id: number, compare_document_id?: number|null, parent_id?: number|null, body: string }} payload */
  async function post(payload) {
    await api.admin.documentChecks.addComment(check.id, payload);
    await refresh();
    toast.success('Kommentar gespeichert');
  }

  async function moderate(comment) {
    try {
      await api.admin.documentChecks.moderateComment(check.id, comment.id, !comment.is_active);
      await refresh();
      toast.success(comment.is_active ? 'Kommentar ausgeblendet' : 'Kommentar wieder eingeblendet');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Kommentar konnte nicht geändert werden.'));
    }
  }

  const all = query.data ?? [];
  const threads = filter ? all.filter((c) => String(c.document_id) === filter || String(c.compare_document_id) === filter) : all;
  const documentOptions = documents.map((d) => ({ value: String(d.id), label: `${d.title || d.original_name} (${d.folder?.name ?? 'Ordner'})` }));

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
      <Card>
        <CardHeader className="flex-row flex-wrap items-end justify-between gap-2">
          <CardTitle as="h2">Kommentare</CardTitle>
          <div className="flex flex-col gap-1">
            <Label htmlFor={filterId} className="text-xs text-muted-foreground">
              Dokument
            </Label>
            <Select id={filterId} size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} wrapperClassName="sm:w-64">
              <option value="">Alle Dokumente</option>
              {documentOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {query.error ? (
            <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
          ) : query.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <CommentThreads
              comments={threads}
              meta={(comment) => (
                <span className="flex flex-wrap items-center gap-1">
                  <Badge variant="outline">{titleOf(comment.document_id)}</Badge>
                  {comment.compare_document_id && <Badge variant="outline">Vergleich mit {titleOf(comment.compare_document_id)}</Badge>}
                </span>
              )}
              onReply={
                canWrite
                  ? (parent, body) =>
                      post({ document_id: parent.document_id, compare_document_id: parent.compare_document_id ?? null, parent_id: parent.id, body })
                  : undefined
              }
              actions={
                canModerate
                  ? (comment) => (
                      <Button size="sm" variant="ghost" onClick={() => moderate(comment)}>
                        {comment.is_active ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                        {comment.is_active ? 'Ausblenden' : 'Einblenden'}
                      </Button>
                    )
                  : undefined
              }
              emptyText="Zu dieser Prüfung gibt es noch keine Kommentare."
            />
          )}
        </CardContent>
      </Card>
      {canWrite && <NewComment documentOptions={documentOptions} defaultDocument={filter} onSubmit={post} />}
    </div>
  );
}

function NewComment({ documentOptions, defaultDocument, onSubmit }) {
  const [documentId, setDocumentId] = useState(defaultDocument);
  const [missing, setMissing] = useState(false);
  const selectId = useId();
  const current = documentId || defaultDocument;
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Neuer Kommentar</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={selectId}>Dokument</Label>
          <Select
            id={selectId}
            value={current}
            aria-invalid={missing || undefined}
            onChange={(e) => {
              setDocumentId(e.target.value);
              setMissing(false);
            }}
          >
            <option value="">Bitte wählen …</option>
            {documentOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {missing && <p className="text-xs font-medium text-destructive">Bitte wählen Sie ein Dokument.</p>}
        </div>
        <CommentForm
          onSubmit={async (body) => {
            if (!current) {
              setMissing(true);
              throw new Error('Bitte wählen Sie ein Dokument.');
            }
            await onSubmit({ document_id: Number(current), body });
          }}
        />
      </CardContent>
    </Card>
  );
}
